import { useState, useEffect } from 'react';

import { Labels, VoucherInfo, VoucherType } from '@/types';

import { useReward } from '@/contexts/reward-context';
import { ShopLayout } from '@/layouts/shop-layout';
import { ArrowDownIcon } from '@/components/svg-icons';
import { VoucherRecordItem } from '../voucher-record-item';

const createTabs = (labels?: Labels) => [
	{ value: 'obtained', label: labels?.lbl_RPObtained },
	{ value: 'used', label: labels?.lbl_RPUsed },
] as const;

type TabType = ReturnType<typeof createTabs>[number]['value'];

const DISPLAY_COUNT = 20;

export const VoucherRecord = () => {
	const { 
		labels,
		getVoucherHistory,
		voucherCondition
	} = useReward();
	const [activeTab, setActiveTab] = useState<TabType>('obtained');
	const [vouchers, setVouchers] = useState<Array<VoucherInfo>>([]);
	const [displayedVouchers, setDisplayedVouchers] = useState<Array<VoucherInfo>>([]);
	const [isAllDataDisplayed, setIsAllDataDisplayed] = useState(false);

	const tabs = createTabs(labels);
	const isUsedTab = activeTab === 'used';

	useEffect(() => {
		getVoucherHistory(isUsedTab).then(vouchers => {
			const filteredVouchers = voucherCondition?.enableFreeBet
				? vouchers
				: vouchers.filter(v => v.VoucherType !== VoucherType.FreeBet);

			setVouchers(filteredVouchers);
			setDisplayedVouchers(filteredVouchers.slice(0, DISPLAY_COUNT));
			setIsAllDataDisplayed(false);
		});
	}, [isUsedTab, voucherCondition]);

	const switchTab = (tabType: TabType) => () => {
		setActiveTab(tabType);
	};

	const loadMore = () => {
		setDisplayedVouchers(vouchers);
		setIsAllDataDisplayed(true);
	};

	return (
		<ShopLayout 
			header={labels?.lbl_RPVoucherRecords}
			containerClassName="reward-single-bg"
		>
			<div className="shop-coupon-records-tab-bar">
				{tabs.map((tab => (
					<div 
						key={tab.value}
						className="shop-coupon-records-tab"
						data-selected={tab.value === activeTab}
						onClick={switchTab(tab.value)}
					>
						<div className="shop-coupon-records-tab__title">
							<span>{tab.label}</span>
						</div>
					</div>
				)))}
			</div>

			{displayedVouchers.length > 0 ? (
				<div className="shop-coupon-records">
					<div className="shop-coupon-records__text">
						<i className="reward-iconcolor-arrangement" />
						{labels?.lbl_RPOrderByLatest}
					</div>

					{displayedVouchers.map((voucher, idx) => (
						<VoucherRecordItem 
							key={`${voucher.Id}_${idx}`} 
							voucher={voucher}
							isInObtainedTab={activeTab === 'obtained'}
						/>
					))}

					{!isAllDataDisplayed && vouchers.length > DISPLAY_COUNT ? (
						<div 
							className="shop-coupon-records__more"
							onClick={loadMore}
						>
							<span className="reward-text">{labels?.lbl_RPMore}</span>
							<ArrowDownIcon className="shop-coupon-records__more-arrow" />
						</div>
					) : null}

					<div className="shop-coupon-records__footer">{labels?.lbl_RPRecords30Day}</div>
				</div>
			) : (
				<div className="reward-empty">
					<div className="reward-empty__img reward-empty__img--record" />
					<div className="reward-empty__text">
						{isUsedTab ? labels?.lbl_RPNoUsageRecords : labels?.lbl_RPNoAcquisitionRecords}
					</div>
				</div>
			)}
		</ShopLayout>
	);
};